import React, { useState, useEffect, useRef, useMemo } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './components/ui/card';
import { Textarea } from './components/ui/textarea';
import { Alert, AlertDescription } from './components/ui/alert';
import { Search, ArrowLeft, Moon, Sun, TrendingUp, TrendingDown, Clock, Trash2, X } from 'lucide-react';
import { Switch } from './components/ui/switch';
import { Label } from './components/ui/label';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function AIScreener() {
  const navigate = useNavigate();
  const [screenerPrompt, setScreenerPrompt] = useState('');
  const [screenerResults, setScreenerResults] = useState(null);
  const [screenerLoading, setScreenerLoading] = useState(false);
  
  // Market cap range inputs
  const [marketCapFrom, setMarketCapFrom] = useState('');
  const [marketCapTo, setMarketCapTo] = useState('');
  
  // Prompt history
  const [promptHistory, setPromptHistory] = useState([]);
  const [aiTaxonomy, setAiTaxonomy] = useState({ sectors: [], tags: [] });
  const [selectedIndustry, setSelectedIndustry] = useState('');
  const [selectedTag, setSelectedTag] = useState('');
  
  // Abort controller for canceling requests
  const abortControllerRef = useRef(null);

  // Session ID for tracking user history (stored in localStorage)
  const [sessionId, setSessionId] = useState(() => {
    let sid = localStorage.getItem('screenSessionId');
    if (!sid) {
      sid = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      localStorage.setItem('screenSessionId', sid);
    }
    return sid;
  });

  // Load history from database on mount
  useEffect(() => {
    const fetchHistory = async () => {
      try {
        const response = await axios.get(`${API}/query-history/${sessionId}`);
        if (response.data.status === 'success') {
          setPromptHistory(response.data.history);
        }
      } catch (error) {
        console.error('Failed to fetch history:', error);
        // Fallback to localStorage if database fails
        const savedHistory = localStorage.getItem('screenPromptHistory');
        if (savedHistory) {
          try {
            setPromptHistory(JSON.parse(savedHistory));
          } catch (e) {
            console.error('Failed to parse localStorage history:', e);
          }
        }
      }
    };
    
    fetchHistory();
  }, [sessionId]);

  useEffect(() => {
    const fetchAiTaxonomy = async () => {
      try {
        const response = await axios.get(`${API}/india/ai-taxonomy`);
        setAiTaxonomy(response.data || { sectors: [], tags: [] });
      } catch (error) {
        console.error('Failed to fetch India AI taxonomy:', error);
      }
    };
    fetchAiTaxonomy();
  }, []);

  const industryOptions = useMemo(() => {
    const names = (aiTaxonomy.sectors || [])
      .flatMap((s) => (s.industries || []).map((i) => i.name))
      .filter(Boolean);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [aiTaxonomy]);

  const tagOptions = useMemo(() => {
    const names = (aiTaxonomy.tags || []).map((t) => t.name).filter(Boolean);
    return [...new Set(names)].sort((a, b) => a.localeCompare(b));
  }, [aiTaxonomy]);

  const handleAIScreener = async () => {
    if (!screenerPrompt.trim()) {
      return;
    }
    
    // Save to database
    try {
      await axios.post(`${API}/query-history`, {
        session_id: sessionId,
        prompt: screenerPrompt,
        market_cap_from: marketCapFrom,
        market_cap_to: marketCapTo
      });
      
      // Update local state optimistically
      const newHistoryItem = {
        id: `temp_${Date.now()}`,
        session_id: sessionId,
        prompt: screenerPrompt,
        market_cap_from: marketCapFrom,
        market_cap_to: marketCapTo,
        timestamp: new Date().toISOString()
      };
      
      setPromptHistory(prev => {
        // Remove duplicate if exists
        const filtered = prev.filter(item => item.prompt !== screenerPrompt);
        // Add to beginning
        return [newHistoryItem, ...filtered].slice(0, 20); // Keep last 20
      });
    } catch (error) {
      console.error('Failed to save query to history:', error);
      // Continue with search even if history save fails
    }
    
    // Create new abort controller
    abortControllerRef.current = new AbortController();
    
    setScreenerLoading(true);
    try {
      const response = await axios.post(`${API}/ai-stock-screener-advanced`, {
        prompt: screenerPrompt,
        market_cap_from: marketCapFrom ? parseFloat(marketCapFrom) * 1000000000 : null,
        market_cap_to: marketCapTo ? parseFloat(marketCapTo) * 1000000000 : null,
        ai_industries: selectedIndustry ? [selectedIndustry] : null,
        ai_tags: selectedTag ? [selectedTag] : null
      }, {
        signal: abortControllerRef.current.signal
      });
      setScreenerResults(response.data);
    } catch (error) {
      if (axios.isCancel(error)) {
        console.log('Search cancelled by user');
        setScreenerResults({
          error: 'Search was cancelled'
        });
      } else {
        console.error('Error in AI stock screener:', error);
        setScreenerResults({
          error: error.response?.data?.detail || 'Failed to screen stocks'
        });
      }
    } finally {
      setScreenerLoading(false);
      abortControllerRef.current = null;
    }
  };

  const handleCancelSearch = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      setScreenerLoading(false);
    }
  };

  const handleHistoryClick = (historyItem) => {
    setScreenerPrompt(historyItem.prompt);
    setMarketCapFrom(historyItem.marketCapFrom || '');
    setMarketCapTo(historyItem.marketCapTo || '');
    // Optionally auto-run the query
    // handleAIScreener();
  };

  const clearHistory = async () => {
    try {
      await axios.delete(`${API}/query-history/${sessionId}`);
      setPromptHistory([]);
      // Also clear localStorage backup
      localStorage.removeItem('screenPromptHistory');
    } catch (error) {
      console.error('Failed to clear history:', error);
      // Clear local state anyway
      setPromptHistory([]);
    }
  };

  return (
    <div className="max-w-[1600px] mx-auto">
        <div className="mb-6">
          <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-blue-800 dark:from-blue-400 dark:to-blue-600 bg-clip-text text-transparent">
            Find Stocks
          </h1>
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
          {/* Left Column - Main Content (75%) */}
          <div className="lg:col-span-3">
            {/* Main Content Card */}
            <Card className="border-2 border-blue-300 dark:border-blue-700">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-2xl">
              <Search className="w-6 h-6 text-blue-600" />
              Advanced Stock Screening
            </CardTitle>
            <CardDescription className="text-base">
              Set market cap range and describe what you're looking for in plain English. Our AI will find matching stocks.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {/* Market Cap Range */}
              <div className="bg-gradient-to-r from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900 p-4 rounded-lg border-2 border-emerald-300 dark:border-emerald-700">
                <h3 className="font-semibold text-emerald-900 dark:text-emerald-100 mb-3 flex items-center gap-2">
                  <TrendingUp className="w-5 h-5" />
                  Market Cap Range (in Billions)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2 block">
                      From ($ Billions)
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g., 1"
                      value={marketCapFrom}
                      onChange={(e) => setMarketCapFrom(e.target.value)}
                      className="border-emerald-300 dark:border-emerald-700 focus:border-emerald-500"
                    />
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">Minimum market cap</p>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-2 block">
                      To ($ Billions)
                    </Label>
                    <Input
                      type="number"
                      placeholder="e.g., 100"
                      value={marketCapTo}
                      onChange={(e) => setMarketCapTo(e.target.value)}
                      className="border-emerald-300 dark:border-emerald-700 focus:border-emerald-500"
                    />
                    <p className="text-xs text-emerald-700 dark:text-emerald-300 mt-1">Maximum market cap</p>
                  </div>
                </div>
              </div>

              {/* India Explorer Filters */}
              <div className="bg-gradient-to-r from-indigo-50 to-indigo-100 dark:from-indigo-950 dark:to-indigo-900 p-4 rounded-lg border-2 border-indigo-300 dark:border-indigo-700">
                <h3 className="font-semibold text-indigo-900 dark:text-indigo-100 mb-3">
                  India Explorer Filters (Industry / Tags)
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 mb-2 block">
                      Industry
                    </Label>
                    <Input
                      list="industry-options"
                      value={selectedIndustry}
                      onChange={(e) => setSelectedIndustry(e.target.value)}
                      placeholder="Type to search industry (blank = all)"
                      className="border-indigo-300 dark:border-indigo-700 focus:border-indigo-500"
                    />
                    <datalist id="industry-options">
                      {industryOptions.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                  </div>
                  <div>
                    <Label className="text-sm font-semibold text-indigo-800 dark:text-indigo-200 mb-2 block">
                      Tag
                    </Label>
                    <Input
                      list="tag-options"
                      value={selectedTag}
                      onChange={(e) => setSelectedTag(e.target.value)}
                      placeholder="Type to search tag (blank = all)"
                      className="border-indigo-300 dark:border-indigo-700 focus:border-indigo-500"
                    />
                    <datalist id="tag-options">
                      {tagOptions.map((name) => (
                        <option key={name} value={name} />
                      ))}
                    </datalist>
                  </div>
                </div>
              </div>

              {/* Example Queries */}
              <div className="bg-blue-50 dark:bg-blue-950 p-4 rounded-lg border border-blue-300 dark:border-blue-700">
                <p className="font-semibold text-sm mb-3 text-blue-900 dark:text-blue-100">
                  Example Queries (Click to use):
                </p>
                <ul className="space-y-2">
                  <li>
                    <button
                      onClick={() => setScreenerPrompt("Find stocks that have been below its 52 week LOW in last 30 days and now it has climbed above that level in last 5 days")}
                      className="w-full text-left p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2 group"
                    >
                      <span className="text-blue-600 font-bold mt-0.5">•</span>
                      <span className="group-hover:underline">
                        Find stocks that have been below its 52 week LOW in last 30 days and now it has climbed above that level in last 5 days
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setScreenerPrompt("Show me stocks trading near their 52-week highs with strong volume in the last week")}
                      className="w-full text-left p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2 group"
                    >
                      <span className="text-blue-600 font-bold mt-0.5">•</span>
                      <span className="group-hover:underline">
                        Show me stocks trading near their 52-week highs with strong volume in the last week
                      </span>
                    </button>
                  </li>
                  <li>
                    <button
                      onClick={() => setScreenerPrompt("Find stocks that dropped 20% from their peak in the last month and have recovered 10% in the last 5 days")}
                      className="w-full text-left p-2 rounded hover:bg-blue-100 dark:hover:bg-blue-900 transition-colors text-sm text-blue-800 dark:text-blue-200 flex items-start gap-2 group"
                    >
                      <span className="text-blue-600 font-bold mt-0.5">•</span>
                      <span className="group-hover:underline">
                        Find stocks that dropped 20% from their peak in the last month and have recovered 10% in the last 5 days
                      </span>
                    </button>
                  </li>
                </ul>
              </div>

              {/* Query Input */}
              <div className="space-y-2">
                <Label className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                  What are you looking for?
                </Label>
                <Textarea
                  placeholder="Example: Find stocks that have been below its 52 week LOW in last 30 days and now it has climbed above that level in last 5 days"
                  value={screenerPrompt}
                  onChange={(e) => setScreenerPrompt(e.target.value)}
                  className="min-h-[120px] text-base border-blue-300 dark:border-blue-700 focus:border-blue-500"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleAIScreener();
                    }
                  }}
                />
              </div>
              
              <div className="flex gap-3">
                <Button 
                  onClick={handleAIScreener}
                  disabled={screenerLoading || !screenerPrompt.trim()}
                  className="bg-blue-600 hover:bg-blue-700 text-lg px-8 py-6 flex-1 md:flex-initial"
                >
                  {screenerLoading ? 'Searching Stocks...' : 'Find Stocks'}
                </Button>
                
                {screenerLoading && (
                  <Button 
                    onClick={handleCancelSearch}
                    variant="destructive"
                    className="text-lg px-6 py-6"
                  >
                    <X className="w-5 h-5 mr-2" />
                    Cancel
                  </Button>
                )}
              </div>

              {/* Results Section */}
              {screenerResults && (
                <div className="mt-8">
                  {screenerResults.error ? (
                    <Alert className="border-red-300 bg-red-50 dark:border-red-800 dark:bg-red-950">
                      <AlertDescription className="text-red-800 dark:text-red-200">
                        {screenerResults.error}
                      </AlertDescription>
                    </Alert>
                  ) : (
                    <div className="space-y-6">
                      {/* Summary Cards */}
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        <Card className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-950 dark:to-blue-900">
                          <CardContent className="p-4">
                            <p className="text-sm text-blue-600 dark:text-blue-400 mb-1">Stocks Analyzed</p>
                            <p className="text-3xl font-bold text-blue-900 dark:text-blue-100">
                              {screenerResults.total_analyzed || 0}
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-gradient-to-br from-emerald-50 to-emerald-100 dark:from-emerald-950 dark:to-emerald-900">
                          <CardContent className="p-4">
                            <p className="text-sm text-emerald-600 dark:text-emerald-400 mb-1">Matches Found</p>
                            <p className="text-3xl font-bold text-emerald-900 dark:text-emerald-100">
                              {screenerResults.matches_found || 0}
                            </p>
                          </CardContent>
                        </Card>
                        
                        <Card className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-950 dark:to-purple-900">
                          <CardContent className="p-4">
                            <p className="text-sm text-purple-600 dark:text-purple-400 mb-1">Success Rate</p>
                            <p className="text-3xl font-bold text-purple-900 dark:text-purple-100">
                              {screenerResults.total_analyzed > 0 
                                ? ((screenerResults.matches_found / screenerResults.total_analyzed) * 100).toFixed(1) 
                                : 0}%
                            </p>
                          </CardContent>
                        </Card>
                      </div>

                      {/* Query Interpretation */}
                      {screenerResults.interpretation && (
                        <Card className="bg-gray-50 dark:bg-gray-900">
                          <CardHeader className="pb-3">
                            <CardTitle className="text-base">AI Query Understanding</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="space-y-2">
                              <p className="text-sm text-gray-700 dark:text-gray-300">
                                <span className="font-semibold">Pattern Detected:</span> {screenerResults.interpretation}
                              </p>
                              {screenerResults.criteria && (
                                <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3 text-xs">
                                  {screenerResults.criteria.pattern_type && (
                                    <div className="bg-blue-100 dark:bg-blue-900 p-2 rounded">
                                      <p className="text-gray-600 dark:text-gray-400">Pattern Type</p>
                                      <p className="font-semibold">{screenerResults.criteria.pattern_type.replace(/_/g, ' ')}</p>
                                    </div>
                                  )}
                                  {screenerResults.criteria.time_period_1 && (
                                    <div className="bg-emerald-100 dark:bg-emerald-900 p-2 rounded">
                                      <p className="text-gray-600 dark:text-gray-400">Primary Period</p>
                                      <p className="font-semibold">{screenerResults.criteria.time_period_1} days</p>
                                    </div>
                                  )}
                                  {screenerResults.criteria.time_period_2 && (
                                    <div className="bg-purple-100 dark:bg-purple-900 p-2 rounded">
                                      <p className="text-gray-600 dark:text-gray-400">Secondary Period</p>
                                      <p className="font-semibold">{screenerResults.criteria.time_period_2} days</p>
                                    </div>
                                  )}
                                  {screenerResults.criteria.threshold_pct && (
                                    <div className="bg-orange-100 dark:bg-orange-900 p-2 rounded">
                                      <p className="text-gray-600 dark:text-gray-400">Threshold</p>
                                      <p className="font-semibold">{screenerResults.criteria.threshold_pct}%</p>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </CardContent>
                        </Card>
                      )}

                      {/* Results Table */}
                      {screenerResults.results && screenerResults.results.length > 0 ? (
                        <Card>
                          <CardHeader>
                            <CardTitle>Matching Stocks</CardTitle>
                          </CardHeader>
                          <CardContent>
                            <div className="overflow-x-auto">
                              <table className="w-full border-collapse">
                                <thead>
                                  <tr className="bg-gray-100 dark:bg-gray-800">
                                    <th className="p-3 text-left text-sm font-semibold">Ticker</th>
                                    <th className="p-3 text-left text-sm font-semibold">Company</th>
                                    <th className="p-3 text-left text-sm font-semibold">Industry</th>
                                    <th className="p-3 text-right text-sm font-semibold">Current Price</th>
                                    
                                    {/* Dynamic columns based on pattern type */}
                                    {screenerResults.criteria?.pattern_type === '52w_high' ? (
                                      <>
                                        <th className="p-3 text-right text-sm font-semibold">High Hit</th>
                                        <th className="p-3 text-right text-sm font-semibold">52W High</th>
                                        <th className="p-3 text-right text-sm font-semibold">High Date</th>
                                        <th className="p-3 text-right text-sm font-semibold">From High</th>
                                      </>
                                    ) : screenerResults.criteria?.pattern_type === 'price_drop' ? (
                                      <>
                                        <th className="p-3 text-right text-sm font-semibold">Peak Price</th>
                                        <th className="p-3 text-right text-sm font-semibold">Peak Date</th>
                                        <th className="p-3 text-right text-sm font-semibold">Drop %</th>
                                      </>
                                    ) : screenerResults.criteria?.pattern_type === 'price_gain' ? (
                                      <>
                                        <th className="p-3 text-right text-sm font-semibold">Low Hit</th>
                                        <th className="p-3 text-right text-sm font-semibold">Low Date</th>
                                        <th className="p-3 text-right text-sm font-semibold">Gain %</th>
                                      </>
                                    ) : (
                                      <>
                                        <th className="p-3 text-right text-sm font-semibold">Low Hit</th>
                                        <th className="p-3 text-right text-sm font-semibold">52W Low</th>
                                        <th className="p-3 text-right text-sm font-semibold">Low Date</th>
                                        <th className="p-3 text-right text-sm font-semibold">Recovery</th>
                                      </>
                                    )}
                                    
                                    <th className="p-3 text-right text-sm font-semibold">Market Cap</th>
                                    <th className="p-3 text-center text-sm font-semibold">Actions</th>
                                  </tr>
                                </thead>
                                <tbody>
                                  {screenerResults.results.map((stock, index) => (
                                    <tr 
                                      key={index} 
                                      className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors"
                                    >
                                      <td className="p-3">
                                        <span className="text-base font-bold text-blue-600 dark:text-blue-400">
                                          {stock.ticker}
                                        </span>
                                      </td>
                                      <td className="p-3 text-sm">{stock.company_name || '—'}</td>
                                      <td className="p-3 text-sm">{stock.ai_industry || '—'}</td>
                                      <td className="p-3 text-right font-semibold">
                                        ₹{stock.current_price?.toFixed(2)}
                                      </td>
                                      
                                      {/* Dynamic data cells based on pattern type */}
                                      {screenerResults.criteria?.pattern_type === '52w_high' ? (
                                        <>
                                          <td className="p-3 text-right text-green-600 dark:text-green-400 font-semibold">
                                            ${stock.actual_high_30d?.toFixed(2) || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-gray-600 dark:text-gray-400 text-sm">
                                            ${stock.week_52_high?.toFixed(2) || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-sm text-gray-600 dark:text-gray-400">
                                            {stock.reference_date || 'N/A'}
                                          </td>
                                          <td className={`p-3 text-right text-base font-bold ${
                                            stock.change_pct >= 0 
                                              ? 'text-green-600 dark:text-green-400' 
                                              : 'text-red-600 dark:text-red-400'
                                          }`}>
                                            {stock.change_pct?.toFixed(1)}%
                                          </td>
                                        </>
                                      ) : screenerResults.criteria?.pattern_type === 'price_drop' ? (
                                        <>
                                          <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-semibold">
                                            ${stock.actual_high_30d?.toFixed(2) || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-sm text-gray-600 dark:text-gray-400">
                                            {stock.reference_date || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-base font-bold text-red-600 dark:text-red-400">
                                            {stock.change_pct?.toFixed(1)}%
                                          </td>
                                        </>
                                      ) : screenerResults.criteria?.pattern_type === 'price_gain' ? (
                                        <>
                                          <td className="p-3 text-right text-gray-600 dark:text-gray-400 font-semibold">
                                            ${stock.actual_low_30d?.toFixed(2) || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-sm text-gray-600 dark:text-gray-400">
                                            {stock.reference_date || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-base font-bold text-green-600 dark:text-green-400">
                                            +{stock.change_pct?.toFixed(1)}%
                                          </td>
                                        </>
                                      ) : (
                                        <>
                                          <td className="p-3 text-right text-red-600 dark:text-red-400 font-semibold">
                                            ${stock.actual_low_30d?.toFixed(2) || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-gray-600 dark:text-gray-400 text-sm">
                                            ${stock.week_52_low?.toFixed(2) || 'N/A'}
                                          </td>
                                          <td className="p-3 text-right text-sm text-gray-600 dark:text-gray-400">
                                            {stock.reference_date || 'N/A'}
                                          </td>
                                          <td className={`p-3 text-right text-base font-bold ${
                                            stock.change_pct > 0 
                                              ? 'text-green-600 dark:text-green-400' 
                                              : 'text-red-600 dark:text-red-400'
                                          }`}>
                                            {stock.change_pct ? `+${stock.change_pct.toFixed(1)}%` : 'N/A'}
                                          </td>
                                        </>
                                      )}
                                      
                                      <td className="p-3 text-right font-semibold">
                                        ₹{(stock.market_cap / 10000000).toFixed(1)} Cr
                                      </td>
                                      <td className="p-3 text-center space-x-2">
                                        <Button
                                          size="sm"
                                          onClick={() => {
                                            navigate(`/public/india-company/${stock.ticker}`);
                                          }}
                                          className="text-xs bg-blue-600 hover:bg-blue-700"
                                        >
                                          Open
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() => {
                                            navigate(`/public/india-company/${stock.ticker}/pitch`);
                                          }}
                                          className="text-xs"
                                        >
                                          Pitch
                                        </Button>
                                      </td>
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </CardContent>
                        </Card>
                      ) : (
                        <Card>
                          <CardContent className="p-8 text-center">
                            <TrendingDown className="w-16 h-16 mx-auto mb-4 text-gray-400" />
                            <p className="text-lg text-gray-600 dark:text-gray-400 mb-2">
                              No stocks found matching your criteria
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-500">
                              Try adjusting your market cap range or modifying your search query
                            </p>
                          </CardContent>
                        </Card>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </CardContent>
        </Card>
          </div>

          {/* Right Column - History Sidebar (25%) */}
          <div className="lg:col-span-1">
            <Card className="border-2 border-purple-300 dark:border-purple-700 sticky top-6">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Clock className="w-4 h-4 text-purple-600" />
                    Query History
                  </CardTitle>
                  {promptHistory.length > 0 && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={clearHistory}
                      className="h-6 w-6 p-0 hover:bg-red-100 dark:hover:bg-red-900"
                      title="Clear history"
                    >
                      <Trash2 className="w-3 h-3 text-red-600" />
                    </Button>
                  )}
                </div>
                <CardDescription className="text-xs">
                  Click to reuse previous queries
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-2 max-h-[calc(100vh-200px)] overflow-y-auto">
                  {promptHistory.length === 0 ? (
                    <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-4">
                      No history yet. Run a query to get started!
                    </p>
                  ) : (
                    promptHistory.map((item, index) => (
                      <button
                        key={index}
                        onClick={() => handleHistoryClick(item)}
                        className="w-full text-left p-3 rounded-lg border border-gray-200 dark:border-gray-700 hover:bg-purple-50 dark:hover:bg-purple-950 hover:border-purple-400 dark:hover:border-purple-600 transition-all group"
                      >
                        <p className="text-xs text-gray-700 dark:text-gray-300 line-clamp-3 group-hover:text-purple-900 dark:group-hover:text-purple-100">
                          {item.prompt}
                        </p>
                        <div className="flex items-center justify-between mt-2">
                          <span className="text-[10px] text-gray-500 dark:text-gray-400">
                            {new Date(item.timestamp).toLocaleDateString()}
                          </span>
                          {(item.marketCapFrom || item.marketCapTo) && (
                            <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-semibold">
                              ${item.marketCapFrom || '0'}B-${item.marketCapTo || '∞'}B
                            </span>
                          )}
                        </div>
                      </button>
                    ))
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
    </div>
  );
}

export default AIScreener;
